import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Stack, Typography, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducers';

import TripCategoryFilter from '../../../Components/TripCategoryFilter';
import TripCard, { TripData } from '../TripCard';
import { request } from '../../../Utils/request';
import { ITrip } from '../Trips';
import { paiseToRupees } from '../../../Utils/currencyConverter';
import { fetchCategories } from '../../../services/CommonApiCall';

interface TripsTabProps {
  onTripEdit: (trip: TripData) => void;
  onTripShare: (trip: TripData) => void;
  onTripClick: (trip: TripData) => void;
}

const LIMIT = 10;

const TripsTab: React.FC<TripsTabProps> = ({
  onTripEdit,
  onTripShare,
  onTripClick,
}) => {
  const dispatch = useDispatch();
  const [trips, setTrips] = useState<ITrip[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const categories = useSelector((state: RootState) => state.category?.categories || []);
  const isLoggedIn = useSelector((state: any) => state.auth.isLoggedIn);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fetchCategories(dispatch, 'trip-itinerary');
  }, [dispatch]);

  const fetchTrips = useCallback(async (page: number = 1, reset: boolean = false) => {
    setLoading(true);
    if (reset) {
      setInitialLoading(true);
    }
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(LIMIT));
    if (selectedCategory && selectedCategory !== 'All') {
      params.set('category', selectedCategory);
    }
    const query = params.toString();
    const url = `/trips/user/my-trips${query ? `?${query}` : ''}`;
    try {
      const res = await request<{
        success: boolean;
        data: ITrip[];
        message: string;
        pagination?: { currentPage: number; totalPages: number; hasNextPage: boolean };
      }>(url, { method: 'GET', authorization: true });
      const tripData = res?.data ?? [];
      if (reset) {
        setTrips(tripData);
      } else {
        setTrips((prev) => [...prev, ...tripData]);
      }
      setHasMore(res?.pagination?.hasNextPage ?? tripData.length >= LIMIT);
    } catch (err) {
      console.log('error', err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (!isLoggedIn) return;
    setCurrentPage(1);
    setTrips([]);
    fetchTrips(1, true);
  }, [isLoggedIn, selectedCategory, fetchTrips]);

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchTrips(nextPage, false);
    }
  }, [loading, hasMore, currentPage, fetchTrips]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );
    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, loadMore]);

  useEffect(() => {
    if (!isLoggedIn) setInitialLoading(false);
  }, [isLoggedIn]);

  return (
    <>
      <TripCategoryFilter
        categories={categories}
        selectedCategory={selectedCategory}
        onCategorySelect={handleCategorySelect}
        showViewAll
      />
      {initialLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {trips.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No trips found
              </Typography>
            </Box>
          ) : (
            <>
              <Stack spacing={2} mb={6}>
                {trips.map((trip) => {
                  const transformedTrip: TripData = {
                    id: trip._id,
                    title: trip.title,
                    location: trip.location?.name || '',
                    duration: `${trip.schedule?.dailySchedule?.length || 0} Days`,
                    price: `₹${paiseToRupees(trip.pricePerPerson).toFixed(2)}/person`,
                    imageUrl: trip.files?.[0]?.fileURL || '',
                    category: trip.category?.[0] || '',
                    type: (trip as any).type || 'Trip',
                    status: (trip as any).status || '',
                  };
                  return (
                    <TripCard
                      key={transformedTrip.id}
                      trip={transformedTrip}
                      variant="trips"
                      onEdit={onTripEdit}
                      onShare={onTripShare}
                      onClick={onTripClick}
                    />
                  );
                })}
              </Stack>

              {hasMore && (
                <Box
                  ref={loadMoreRef}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 3,
                    minHeight: '60px',
                  }}
                >
                  {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Loading more trips...
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {!hasMore && trips.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No more trips to load
                  </Typography>
                </Box>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default TripsTab;
